import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Loader2, Lock, User, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import { useStudentAuth } from "@/lib/studentAuth";
import { formatApiErrorDetail } from "@/lib/auth";
import { SITE } from "@/lib/site";

export default function StudentLogin() {
  const { student, login } = useStudentAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (student) return <Navigate to="/student/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) {
      toast.error("Username and password are required.");
      return;
    }
    setLoading(true);
    try {
      await login(username.trim().toLowerCase(), password);
      toast.success("Welcome to your exam portal!");
      navigate("/student/dashboard", { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "Login failed";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center px-5 py-10">
      <div className="w-full max-w-md">
        <a href="/" className="flex items-center justify-center mb-8" data-testid="student-back-home-link">
          <img src={SITE.logo.horizontal} alt={SITE.name} className="h-12 w-auto object-contain" />
        </a>
        <div className="bg-white rounded-3xl shadow-xl border border-slate-100 p-8 md:p-10">
          <div className="w-12 h-12 rounded-2xl bg-[#73c3ff] text-[#002570] flex items-center justify-center mb-5">
            <GraduationCap className="w-6 h-6" />
          </div>
          <h1 className="font-rubik text-2xl md:text-3xl font-bold text-[#0f172a]">Student Portal</h1>
          <p className="mt-2 font-inter text-sm text-slate-600">Sign in to take your mock exam and view past attempts.</p>

          <form onSubmit={submit} className="mt-7 space-y-4" data-testid="student-login-form">
            <div>
              <label className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="student-username">Username</label>
              <div className="mt-1.5 relative">
                <User className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  id="student-username"
                  data-testid="student-username-input"
                  type="text"
                  required
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="your-username"
                  className="w-full bg-white rounded-xl border border-slate-200 pl-11 pr-4 py-3 font-inter text-sm text-slate-800 focus:outline-none focus:ring-4 focus:ring-[#73c3ff]/30 focus:border-[#73c3ff] transition"
                />
              </div>
            </div>
            <div>
              <label className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="student-password">Password</label>
              <div className="mt-1.5 relative">
                <Lock className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  id="student-password"
                  data-testid="student-password-input"
                  type="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-white rounded-xl border border-slate-200 pl-11 pr-4 py-3 font-inter text-sm text-slate-800 focus:outline-none focus:ring-4 focus:ring-[#73c3ff]/30 focus:border-[#73c3ff] transition"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="student-login-submit-btn"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#002570] text-white hover:bg-[#001a52] disabled:opacity-70 rounded-full px-7 py-3.5 font-inter font-medium transition-all"
            >
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Signing in…</> : "Sign In"}
            </button>
          </form>
          <p className="mt-5 font-inter text-xs text-slate-500 text-center">
            Don't have credentials yet? Ask your faculty at Educators Institute to issue your login.
          </p>
        </div>
        <p className="mt-6 text-center font-inter text-xs text-slate-500">
          <a href="/" className="text-[#002570] font-medium hover:underline">← Back to website</a>
        </p>
      </div>
    </div>
  );
}
